import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { ErrorCode } from 'react-dropzone';
import { ACCEPT_IMAGE_EXTENTIONS_MAGRITTE, ImageType } from 'utils/constants/banner';
import { Settings } from 'utils/constants/dynamicSettings';
import { MagritteNotificationName } from 'utils/constants/notification';

import bannerImageFirstUploadButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/common/banner_image_first_upload_button_click';
import { Button, Text, Upload, VSpacing } from '@hh.ru/magritte-ui';
import type { LoadedFile } from '@hh.ru/magritte-ui';
import type { Accept } from '@hh.ru/magritte-ui-upload/node_modules/react-dropzone';

import useTrls from 'hooks/translation/useTrls';
import useSettings from 'hooks/useSettings';

import { GalleryPreview } from 'components/common/BannerImg/GalleryPreview/GalleryPreview';
import { loadImage } from 'components/common/BannerImg/loadImage';
import { TechnicalRequirements } from 'components/common/BannerImg/TechnicalRequirements/TechnicalRequirements';

import { setIsCropImageModalVisible } from 'store/models/createBanner';
import { setIsGalleryModalVisible } from 'store/models/gallery';
import { addMagritteNotification } from 'store/models/notification';
import { useDispatch } from 'store/utils';

interface UploadFieldProps {
    onlyField?: boolean;
}

const ONE_KB = 1024;

const TrlKeys = {
    title: 'AdCreate.AdForm.BannerImg.title.notLoaded',
    subtitle: 'AdCreate.AdForm.BannerImg.advice.subtitle',
    description: 'AdCreate.AdForm.BannerImg.advice.description',
    button: 'AdCreate.AdForm.BannerImg.UploadField.button',
    renderFileInfo: {
        loading: 'AdCreate.AdForm.BannerImg.UploadField.renderFileInfo.loading',
        loaded: 'AdCreate.AdForm.BannerImg.UploadField.renderFileInfo.loaded',
        error: 'AdCreate.AdForm.BannerImg.UploadField.renderFileInfo.error',
    },
    select: 'AdCreate.AdForm.BannerImg.UploadField.select',
    gallery: {
        preview: 'AdCreate.AdForm.BannerImg.gallery.preview',
    },
};

export const UploadField: FC<UploadFieldProps> = ({ onlyField = false }) => {
    const [isLoading, setLoading] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState<LoadedFile[]>([]);

    const dispatch = useDispatch();
    const trl = useTrls();
    const settings = useSettings();

    const maxSizeInKiloByte = settings(Settings.WebdavFileMaxSize) as number;
    const maxSizeInBytes = maxSizeInKiloByte * ONE_KB;

    const handleSelectOnComputerClick = useCallback(() => {
        if (!onlyField) {
            bannerImageFirstUploadButtonClick({
                buttonName: 'choose_on_computer',
            });
        }
    }, [onlyField]);

    const handleFileUpload = useCallback(
        (file: File) => {
            if (onlyField) {
                dispatch(setIsGalleryModalVisible(false));
            }
            void dispatch(
                loadImage({
                    imageType: ImageType.User,
                    fileName: file.name,
                    file,
                    setLoading,
                    callBack: () => dispatch(setIsCropImageModalVisible(true)),
                    onProgress: (percentage) => {
                        setUploadedFiles((prevFiles) => {
                            if (!prevFiles.length) {
                                return prevFiles;
                            }
                            return prevFiles.map((file) => ({
                                ...file,
                                loadPercentage: percentage,
                            }));
                        });
                    },
                })
            );
        },
        [dispatch, onlyField]
    );

    const magritteTrls = useMemo(
        () => ({
            title: trl(TrlKeys.select),
            subTitle: trl(TrlKeys.subtitle),
        }),
        [trl]
    );

    const handleMagritteDropAccepted = useCallback(
        (files: LoadedFile[]) => {
            const file = files[0]?.data;
            if (!file) {
                return;
            }
            setUploadedFiles([{ ...files[0], loading: true }]);
            handleFileUpload(file);
        },
        [handleFileUpload]
    );

    const handleMagritteDropRejected = useCallback(
        (files: LoadedFile[]) => {
            const errorCode = files?.[0]?.error;
            if (errorCode === ErrorCode.FileTooLarge) {
                dispatch(addMagritteNotification(MagritteNotificationName.CampaignFormImageTooBigSize));
                return;
            }
            if (errorCode === ErrorCode.FileInvalidType) {
                dispatch(addMagritteNotification(MagritteNotificationName.CampaignFormImageWrongExtension));
            }
        },
        [dispatch]
    );

    const handleMagritteFileDelete = useCallback(() => {
        setUploadedFiles([]);
        setLoading(false);
    }, []);

    useEffect(() => {
        setUploadedFiles((prevFiles) => {
            if (!prevFiles.length) {
                return prevFiles;
            }
            if (!isLoading) {
                return [];
            }
            return prevFiles.map((file) => ({
                ...file,
                loading: true,
            }));
        });
    }, [isLoading]);

    const uploadImageField = (
        <>
            <Text typography="label-2-regular">{trl(TrlKeys.description)}</Text>
            <VSpacing default={12} />
            <TechnicalRequirements />
            <VSpacing default={24} />
            <Upload
                size="small"
                files={uploadedFiles}
                trls={magritteTrls}
                accept={ACCEPT_IMAGE_EXTENTIONS_MAGRITTE as Accept}
                maxSize={maxSizeInBytes}
                selectFileButton={
                    <Button mode="secondary" size="small" style="accent" onClick={handleSelectOnComputerClick}>
                        {trl(TrlKeys.button)}
                    </Button>
                }
                renderFileInfo={(loadedFile) => {
                    const { loading, loadPercentage, loadTime, fileSizeMB, error } = loadedFile;
                    if (error) {
                        return trl(TrlKeys.renderFileInfo.error, [error]);
                    }
                    if (loading) {
                        return trl(TrlKeys.renderFileInfo.loading, [loadPercentage, fileSizeMB]);
                    }
                    return trl(TrlKeys.renderFileInfo.loaded, [fileSizeMB, loadTime]);
                }}
                onDropAccepted={handleMagritteDropAccepted}
                onDropRejected={handleMagritteDropRejected}
                onFileDelete={handleMagritteFileDelete}
                data-qa="image"
            />
        </>
    );

    if (onlyField) {
        return uploadImageField;
    }

    return (
        <>
            <Text style="secondary" typography="label-2-regular">
                {trl(TrlKeys.title)}
            </Text>
            <VSpacing default={12} />
            {uploadImageField}
            <VSpacing default={24} />
            <GalleryPreview />
        </>
    );
};
